import { useEffect, useId, useRef, useState } from 'react';

import { AlertIcon, CheckIcon, ChevronRightIcon, InfoIcon, QuestionIcon } from './icons.jsx';
import { useT } from '../lib/i18n.js';

/*
 * Layout and help primitives (docs/dev/ui-guidelines.md). The rule they serve: a page shows names,
 * numbers, states and actions; explanations sit one click away behind a "?" or a disclosure.
 * Re-exported from ui.jsx, so pages import everything from there.
 */

/** Short help text: one line under a field (Field `help`), longer goes into a `tip`. */
export const HELP_MAX = 120;
/** A page header's visible line. */
export const LEAD_MAX = 90;

/**
 * Dev-only nudge for area workers: names the component and the start of the over-long text once per
 * text, so the console lists every place that still carries a paragraph.
 */
const warned = new Set();
export function warnLong(where, text, max) {
  if (!import.meta.env?.DEV || typeof text !== 'string' || text.length <= max) return;
  const key = `${where}:${text}`;
  if (warned.has(key)) return;
  warned.add(key);
  console.warn(
    `[ui] ${where} text is ${text.length} chars (max ${max}); move the rest into a tip: "${text.slice(0, 60)}…"`,
  );
}

/**
 * The "?" button that opens a small popover with help. Keyboard: Enter/Space opens, Escape closes
 * and puts focus back on the button, Tab moves into the popover (links inside stay reachable).
 * Clicking outside closes it. The button is named `label` ("More info"), not after the field: a
 * name containing the field's label would make `getByLabel('Memory')` match the button too. Pass
 * `describedBy` (the id of the label or heading) so screen readers still say what it is about.
 * `docs` (an href) adds a "Learn more" link. `align`: 'start' (default) or 'end' (right edge).
 */
export function HelpTip({ label, describedBy, children, docs, align, className = '' }) {
  const t = useT();
  label ??= t('help.more_info');
  const [open, setOpen] = useState(false);
  const [side, setSide] = useState(align ?? 'start');
  const id = useId();
  const button = useRef(null);
  const box = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!box.current?.contains(e.target) && !button.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const toggle = () => {
    if (!open && !align && button.current) {
      // Open towards the roomier side so the popover never runs off the screen.
      const r = button.current.getBoundingClientRect();
      setSide(r.left > window.innerWidth / 2 ? 'end' : 'start');
    }
    setOpen((v) => !v);
  };
  const onKeyDown = (e) => {
    if (e.key === 'Escape' && open) {
      e.stopPropagation(); // don't close a surrounding dialog too
      setOpen(false);
      button.current?.focus();
    }
  };

  return (
    <span className={`relative inline-flex align-middle ${className}`} onKeyDown={onKeyDown}>
      <button
        ref={button}
        type="button"
        aria-label={label}
        aria-describedby={describedBy}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={toggle}
        className="inline-flex size-5 items-center justify-center rounded-full border border-line-strong text-[11px] leading-none font-bold text-muted hover:border-accent hover:text-accent aria-expanded:border-accent aria-expanded:text-accent"
      >
        <QuestionIcon size="size-3" />
      </button>
      {open ? (
        <span
          ref={box}
          id={id}
          role="note"
          className={`absolute top-full z-30 mt-2 block w-max max-w-[min(22rem,80vw)] rounded-md border border-line bg-surface px-3 py-2 text-left text-sm font-normal text-text shadow-lg ${side === 'end' ? 'right-0' : 'left-0'}`}
        >
          <span className="block whitespace-normal">{children}</span>
          {docs ? (
            <a href={docs} target="_blank" rel="noreferrer" className="mt-1.5 inline-block text-accent underline">
              {t('help.learn_more')}
            </a>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}

/**
 * A collapsible block ("Advanced", "Details", "Learn more"): a native <details>, so it works with
 * the keyboard and screen readers as-is. `defaultOpen` starts it open; `onToggle(open)` reports changes.
 */
export function Disclosure({
  summary = 'Advanced',
  children,
  defaultOpen = false,
  reopenKey,
  className = '',
  onToggle,
}) {
  // `open` is only set on mount: passing it on every render would let React close the block under the user
  // (and the control they're on) when `defaultOpen` turns false. A later `defaultOpen` of true opens it, and so
  // does a new `reopenKey` while `defaultOpen` is true: the browser reports a close in a later task, so a caller
  // that reopens in the meantime never sees `defaultOpen` change.
  const [initiallyOpen] = useState(Boolean(defaultOpen));
  const ref = useRef(null);
  useEffect(() => {
    if (defaultOpen && ref.current && !ref.current.open) ref.current.open = true;
  }, [defaultOpen, reopenKey]);
  return (
    <details
      ref={ref}
      className={`group ${className}`}
      open={initiallyOpen || undefined}
      onToggle={onToggle ? (e) => onToggle(e.currentTarget.open) : undefined}
    >
      <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 text-sm font-semibold text-muted select-none hover:text-text [&::-webkit-details-marker]:hidden">
        <span aria-hidden="true" className="inline-flex transition-transform group-open:rotate-90">
          <ChevronRightIcon size="size-3" />
        </span>
        {summary}
      </summary>
      <div className="mt-3">{children}</div>
    </details>
  );
}

const NOTICE_TONES = {
  info: ['border-line bg-sunk', 'text-muted', InfoIcon],
  ok: ['border-kelp/50 bg-kelp/10', 'text-kelp', CheckIcon],
  warn: ['border-amber/50 bg-amber/10', 'text-amber', AlertIcon],
  danger: ['border-danger/50 bg-danger/10', 'text-danger', AlertIcon],
};

/**
 * One or two lines of status ("Installs fail until an account is picked"). Longer explanations go
 * into `details` (a disclosure inside the notice). `action` is a button or link at the right.
 * `role="status"`/"alert" is up to the caller (`role` prop) — most notices are static.
 */
export function InlineNotice({ tone = 'info', children, details, action, className = '', role }) {
  const [box, iconColour, Icon] = NOTICE_TONES[tone] ?? NOTICE_TONES.info;
  return (
    <div role={role} className={`flex items-start gap-2.5 rounded-md border px-3 py-2 text-sm ${box} ${className}`}>
      <span aria-hidden="true" className={`mt-0.5 inline-flex w-3.5 shrink-0 justify-center ${iconColour}`}>
        <Icon size="size-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div>{children}</div>
        {details ? (
          <Disclosure summary="Details" className="mt-1">
            <div className="text-muted">{details}</div>
          </Disclosure>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

/**
 * Responsive form grid: one column on phones, `cols` (2 by default, or 3) from `md` up. Give a child
 * `className="md:col-span-2"` (or the `FormGrid.Full` wrapper) to span the row.
 */
export function FormGrid({ cols = 2, children, className = '' }) {
  const grid = { 1: '', 2: 'md:grid-cols-2', 3: 'md:grid-cols-2 xl:grid-cols-3' }[cols] ?? 'md:grid-cols-2';
  return <div className={`grid grid-cols-1 gap-x-5 gap-y-4 ${grid} ${className}`}>{children}</div>;
}
FormGrid.Full = function FormGridFull({ children }) {
  return <div className="md:col-span-full">{children}</div>;
};

/**
 * Read-only facts as label → value pairs (a <dl>), instead of sentences like "This node runs X with
 * Y". `items`: [{ label, value, mono?, tip?, hidden? }] (falsy entries are skipped). `cols`: 1, 2 or 3
 * columns on wide screens.
 */
export function DetailList({ items, cols = 2, className = '' }) {
  const grid = { 1: '', 2: 'md:grid-cols-2', 3: 'md:grid-cols-2 xl:grid-cols-3' }[cols] ?? 'md:grid-cols-2';
  return (
    <dl className={`grid grid-cols-1 gap-x-8 gap-y-2 text-sm ${grid} ${className}`}>
      {items
        .filter((i) => i && !i.hidden)
        .map((i) => (
          <div key={i.key ?? i.label} className="flex min-w-0 items-baseline gap-3">
            <dt className="flex w-36 shrink-0 items-center gap-1.5 text-muted">
              {i.label}
              {i.tip ? <HelpTip>{i.tip}</HelpTip> : null}
            </dt>
            <dd className={`min-w-0 break-words ${i.mono ? 'font-mono text-xs leading-5' : ''}`}>{i.value ?? '—'}</dd>
          </div>
        ))}
    </dl>
  );
}

/**
 * The bar above a list: filters/search on the left, actions on the right; wraps on small screens.
 * `label` names it for screen readers (role="toolbar" is not used: it would demand arrow-key moves).
 */
export function Toolbar({ children, actions, label, className = '' }) {
  return (
    <div
      role={label ? 'group' : undefined}
      aria-label={label}
      className={`mb-4 flex flex-wrap items-end gap-3 ${className}`}
    >
      <div className="flex min-w-0 flex-1 flex-wrap items-end gap-3">{children}</div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}
